import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Check, X, Edit2, Zap, User, Plus } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { toast } from 'sonner';
import api, { apiError, fmtDateTime, parseUTC } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';

const STATUS_COLOR = {
  approved: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  pending: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  rejected: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
  withdrawn: 'bg-slate-500/15 text-slate-400 border-slate-500/30',
};

export default function Accounting() {
  const { hasPerm } = useAuth();
  // Vente du bot : seul un manager (sales_manage) peut rattacher ou changer le fan ; le chatteur, jamais.
  const peutRattacherBot = typeof hasPerm === 'function' && hasPerm('sales_manage');
  const [sales, setSales] = useState([]);
  const [team, setTeam] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [filters, setFilters] = useState({ employee: 'all', model: 'all', status: 'all', period: 'all' });

  const load = async () => {
    try {
      const { data } = await api.get('/sales');
      setSales(data);
    } catch (e) {
      toast.error(apiError(e, 'Impossible de charger les ventes'));
    }
    api.get('/chatters').then(({ data }) => setTeam(data)).catch(() => {});
    api.get('/accounts').then(({ data }) => setAccounts(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const periodSince = useMemo(() => {
    const now = new Date();
    if (filters.period === 'today') { const d = new Date(now); d.setHours(0,0,0,0); return d; }
    const m = filters.period.match(/^(\d+)d$/);
    if (m) return new Date(now.getTime() - parseInt(m[1], 10) * 86400000);
    return null;
  }, [filters.period]);

  const filtered = useMemo(() => sales.filter(s =>
    (filters.employee === 'all' || s.chatter_id === filters.employee) &&
    (filters.model === 'all' || s.account_id === filters.model) &&
    (filters.status === 'all' || s.status === filters.status) &&
    (!periodSince || parseUTC(s.date) >= periodSince)
  ), [sales, filters, periodSince]);

  const totalApproved = useMemo(() => filtered.filter(s => s.status === 'approved').reduce((sum, x) => sum + x.amount, 0), [filtered]);
  const approvedCount = useMemo(() => filtered.filter(s => s.status === 'approved').length, [filtered]);

  // Render only the first N rows and grow on scroll — rendering all ~1.7k+ sale rows at
  // once froze the page on open. KPIs/totals above still use the FULL filtered set.
  const [visibleCount, setVisibleCount] = useState(100);
  useEffect(() => { setVisibleCount(100); }, [filters]);
  const sentinelRef = useRef(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) setVisibleCount(v => Math.min(v + 100, filtered.length));
    }, { rootMargin: '600px' });
    io.observe(el);
    return () => io.disconnect();
  }, [filtered.length, visibleCount]);

  const update = async (id, status) => {
    try {
      const { data } = await api.patch(`/sales/${id}`, { status });
      setSales(sales.map(s => s.id === id ? data : s));
      const statusLabel = { approved: 'approuvée', rejected: 'rejetée', pending: 'remise en attente' }[status] || status;
      toast.success(`Vente ${statusLabel}`);
    } catch (e) {
      toast.error(apiError(e, 'Échec de la mise à jour'));
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto fade-in" data-testid="accounting-page">
      <PageHeader
        title="Ventes"
        subtitle={`Suis, attribue et valide les ventes · ${filtered.length} ventes`}
        onRefresh={load}
        actions={<ReportSale team={team} accounts={accounts} onCreated={load} />}
      />

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 mb-4 flex flex-wrap items-center gap-2">
        <Sel testid="filter-period" label="Tout l'historique" value={filters.period} onChange={v => setFilters({ ...filters, period: v })} options={[['today', "Aujourd'hui"], ['7d', '7 derniers jours'], ['14d', '14 derniers jours'], ['30d', '30 derniers jours']]} />
        <Sel testid="filter-employee" label="Tous les employés" value={filters.employee} onChange={v => setFilters({ ...filters, employee: v })} options={team.map(t => [t.id, t.name])} />
        <Sel testid="filter-model" label="Tous les modèles" value={filters.model} onChange={v => setFilters({ ...filters, model: v })} options={accounts.map(a => [a.id, a.name])} />
        <Sel testid="filter-status" label="Tous les statuts" value={filters.status} onChange={v => setFilters({ ...filters, status: v })} options={[['pending', 'En attente'], ['approved', 'Approuvée'], ['rejected', 'Rejetée'], ['withdrawn', 'Retirée']]} />
        <div className="ml-auto flex items-center gap-4 text-xs text-slate-500">
          <span data-testid="approved-count"><span className="text-slate-300 font-semibold">{approvedCount}</span> ventes approuvées</span>
          <span>Total approuvé : <span className="text-emerald-400 font-bold" data-testid="total-approved">{totalApproved.toLocaleString()} €</span></span>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-x-auto">
        <table className="w-full min-w-[820px]">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-white/5">
              <th className="px-5 py-3 font-semibold">Fan</th>
              <th className="px-5 py-3 font-semibold">Modèle</th>
              <th className="px-5 py-3 font-semibold">Déclaré par</th>
              <th className="px-5 py-3 font-semibold">Source</th>
              <th className="px-5 py-3 font-semibold">Date</th>
              <th className="px-5 py-3 font-semibold text-right">Montant</th>
              <th className="px-5 py-3 font-semibold">Statut</th>
              <th className="px-5 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {filtered.slice(0, visibleCount).map(s => {
              const acc = accounts.find(a => a.id === s.account_id);
              return (
                <tr key={s.id} className="hover:bg-white/[0.02]" data-testid={`sale-row-${s.id}`}>
                  <td className="px-5 py-3 text-sm text-white" data-testid={`sale-fan-${s.id}`}>
                    <div className="flex items-center gap-2">
                      <span className={s.conversation_id ? '' : 'text-amber-400'}>{s.fan_name || s.client || '—'}</span>
                      {(!estVenteBot(s) || peutRattacherBot) && <AttributeFan sale={s} accounts={accounts} onDone={(d) => setSales(prev => prev.map(x => x.id === d.id ? d : x))} />}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-sm text-slate-300">{acc?.name || <span className="text-amber-400/80">à attribuer</span>}</td>
                  <td className="px-5 py-3 text-sm text-slate-300">
                    {estVenteBot(s) ? (
                      // Bot d'accueil : la 1re vente d'un fan accueilli par le bot lui est créditée, jamais à un chatteur.
                      <span className="inline-flex flex-col gap-0.5" data-testid={`sale-bot-${s.id}`}>
                        <span className="text-[11px] px-2 py-0.5 rounded-md bg-violet-500/15 text-violet-300 border border-violet-500/30 inline-flex items-center gap-1 w-fit">🤖 Bot</span>
                        {!s.conversation_id && <span className="text-[10px] text-amber-300">fan à identifier</span>}
                      </span>
                    ) : (
                      <ReassignChatter sale={s} team={team} onDone={(d) => setSales(prev => prev.map(x => x.id === d.id ? d : x))} />
                    )}
                  </td>
                  <td className="px-5 py-3"><Badge variant="outline" className={s.source === 'auto' ? 'border-amber-500/30 bg-amber-500/10 text-amber-300' : 'border-indigo-500/30 bg-indigo-500/10 text-indigo-300'}>{s.source === 'auto' ? <><Zap size={10} className="mr-1" /> auto</> : <><User size={10} className="mr-1" /> {s.source === 'manual_report' ? 'déclarée' : 'manuelle'}</>}</Badge></td>
                  <td className="px-5 py-3 text-xs text-slate-500">{fmtDateTime(s.date)}</td>
                  <td className="px-5 py-3 text-right text-sm font-bold text-white">{s.amount} €</td>
                  <td className="px-5 py-3"><Badge className={STATUS_COLOR[s.status]} data-testid={`sale-status-${s.id}`}>{({ approved: 'approuvée', pending: 'en attente', rejected: 'rejetée', withdrawn: 'retirée' })[s.status] || s.status}</Badge></td>
                  <td className="px-5 py-3 text-right">
                    {s.status === 'pending' ? (
                      <div className="flex gap-1 justify-end">
                        <Button data-testid={`approve-sale-${s.id}`} size="sm" className="bg-emerald-500 hover:bg-emerald-600 h-7 text-xs px-2" onClick={() => update(s.id, 'approved')}><Check size={11} className="mr-1" /> Approuver</Button>
                        <Button data-testid={`reject-sale-${s.id}`} size="sm" variant="outline" className="border-rose-500/30 bg-rose-500/10 text-rose-300 hover:bg-rose-500/20 h-7 text-xs px-2" onClick={() => update(s.id, 'rejected')}><X size={11} className="mr-1" /> Rejeter</Button>
                      </div>
                    ) : (
                      <Button size="sm" variant="ghost" className="text-slate-400 hover:bg-white/5 h-7 text-xs" onClick={() => update(s.id, 'pending')}><Edit2 size={11} className="mr-1" /> Rouvrir</Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {visibleCount < filtered.length && (
          <div ref={sentinelRef} className="py-4 text-center text-xs text-slate-500" data-testid="accounting-load-more">
            Chargement… ({visibleCount}/{filtered.length})
          </div>
        )}
      </div>
    </div>
  );
}

// Vente faite sur le lien du bot d'accueil (raison posée par le serveur).
const estVenteBot = (s) => String(s.reason || '').startsWith('auto:bot_');

// Corriger QUI a fait la vente (owner/manager). L'attribution auto peut se tromper
// (lien partagé, e-mail mappé à une autre conv) : ici on remet la bonne personne en 1 clic.
function ReassignChatter({ sale, team, onDone }) {
  const [saving, setSaving] = useState(false);
  // 14/10 (C-506) — Marlon : « Déclaré par … ça met Non attribué. J'aimerais que ça mette Bot. » Une vente du bot
  // n'a pas de chatteur (`chatter_id` vide) mais porte sa signature (« 🤖 Bot HERA ») : on l'affiche.
  const venteDuBot = !sale.chatter_id && String(sale.chatter_name || '').startsWith('🤖');
  const change = async (val) => {
    if (val === (sale.chatter_id || '')) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/sales/${sale.id}`, { chatter_id: val });
      onDone(data);
      toast.success(val ? `Vente attribuée à ${data.chatter_name || 'ce chatteur'}` : 'Chatteur retiré');
    } catch (e) { toast.error(apiError(e, "Échec du changement de chatteur")); }
    finally { setSaving(false); }
  };
  return (
    <select
      value={sale.chatter_id || ''}
      onChange={(e) => change(e.target.value)}
      disabled={saving}
      data-testid={`reassign-chatter-${sale.id}`}
      title="Changer qui a fait cette vente"
      className={`bg-transparent border border-white/10 rounded-lg px-2 py-1 text-sm outline-none focus:border-indigo-500/40 max-w-[170px] cursor-pointer disabled:opacity-50 ${sale.chatter_id ? 'text-slate-200' : venteDuBot ? 'text-violet-300' : 'text-amber-400'}`}
    >
      <option value="" className="bg-[#0B0B0E]">{venteDuBot ? '🤖 Bot' : 'Non attribué'}</option>
      {(team || []).map((c) => <option key={c.id} value={c.id} className="bg-[#0B0B0E]">{c.name}</option>)}
    </select>
  );
}

// Attribuer une vente à un fan (et donc au bon modèle) — surtout pour les paiements PayPal
// qui arrivent sans fan/modèle. On choisit le modèle (filtre la liste) → on cherche le fan →
// le backend met la vente sur le fan ET sur son modèle, et crédite son « total dépensé ».
function AttributeFan({ sale, accounts, onDone }) {
  const bot = estVenteBot(sale);
  const [open, setOpen] = useState(false);
  const [model, setModel] = useState(sale.account_id || '');
  const [convs, setConvs] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !model) { setConvs([]); return undefined; }
    let alive = true;
    setLoading(true);
    api.get('/conversations', { params: { account_id: model } })
      .then(({ data }) => { if (alive) setConvs(data || []); })
      .catch(() => { if (alive) setConvs([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, model]);

  const list = (q ? convs.filter(c => (c.name || '').toLowerCase().includes(q.toLowerCase())) : convs).slice(0, 60);

  const attach = async (convId, force = false) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/sales/${sale.id}`, { conversation_id: convId, ...(force ? { force_conflict: true } : {}) });
      toast.success('Vente rattachée au fan');
      setOpen(false);
      onDone(data);
    } catch (e) {
      // Garde-fou anti-mauvais-fan (409) : demander confirmation puis forcer.
      if (e?.response?.status === 409 && !force) {
        const msg = apiError(e, 'Ce payeur est déjà relié à un autre fan.');
        if (window.confirm(`${msg}\n\nRattacher quand même à ce fan ?`)) { await attach(convId, true); return; }
      } else {
        toast.error(apiError(e, 'Échec du rattachement'));
      }
    }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`attribute-fan-${sale.id}`} className="text-[11px] text-indigo-300 hover:underline shrink-0">{sale.conversation_id ? 'changer' : bot ? 'rattacher' : 'attribuer'}</button>
      </DialogTrigger>
      <DialogContent className="bg-[#0B0B0E] border-white/10 text-slate-200">
        <DialogHeader><DialogTitle className="text-white">Attribuer la vente à un fan</DialogTitle></DialogHeader>
        <div className="space-y-3 pt-1">
          <div className="text-xs text-slate-400">{sale.amount} € · {sale.client || '—'}</div>
          {bot && <div className="text-[11px] text-violet-300" data-testid="attribute-fan-bot">🤖 Elle reste créditée au Bot.</div>}
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs">Modèle</Label>
            <select data-testid="attribute-model" value={model} onChange={e => { setModel(e.target.value); setQ(''); }} disabled={bot && !!sale.account_id} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white h-10 outline-none disabled:opacity-60">
              <option value="" className="bg-[#0B0B0E]">Choisir un modèle…</option>
              {accounts.map(a => <option key={a.id} value={a.id} className="bg-[#0B0B0E]">{a.name}</option>)}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs">Fan</Label>
            <Input data-testid="attribute-fan-search" value={q} onChange={e => setQ(e.target.value)} placeholder={model ? 'Rechercher un fan…' : "Choisis d'abord un modèle"} className="bg-white/5 border-white/10 text-white h-10" disabled={!model} />
          </div>
          <div className="max-h-64 overflow-y-auto rounded-lg border border-white/10 divide-y divide-white/5">
            {loading && <div className="px-3 py-3 text-xs text-slate-500">Chargement…</div>}
            {!loading && model && list.length === 0 && <div className="px-3 py-3 text-xs text-slate-500">Aucun fan trouvé.</div>}
            {list.map(c => (
              <button key={c.id} disabled={saving} onClick={() => attach(c.id)} className="w-full text-left px-3 py-2 text-sm text-white hover:bg-white/5 disabled:opacity-50">
                {c.name || c.telegram_username || c.id}
              </button>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReportSale({ team, accounts, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ account_id: '', chatter_id: '', client: '', amount: '', type: 'purchase', link: '' });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.account_id || !form.client || !form.amount) { toast.error('Compte, client et montant obligatoires'); return; }
    setSaving(true);
    try {
      await api.post('/sales', { ...form, amount: parseFloat(form.amount), chatter_id: form.chatter_id || null });
      setOpen(false); setForm({ account_id: '', chatter_id: '', client: '', amount: '', type: 'purchase', link: '' });
      toast.success('Vente déclarée — en attente de validation');
      onCreated();
    } catch (e) {
      toast.error(apiError(e, 'Impossible de déclarer la vente'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="report-sale-button" className="bg-indigo-500 hover:bg-indigo-600"><Plus size={16} className="mr-1.5" /> Déclarer une vente</Button></DialogTrigger>
      <DialogContent className="bg-[#0B0B0E] border-white/10 text-slate-200">
        <DialogHeader><DialogTitle className="text-white">Déclarer une vente</DialogTitle></DialogHeader>
        <div className="space-y-3 pt-2">
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs">Compte Telegram</Label>
            <select data-testid="report-sale-account" value={form.account_id} onChange={e => setForm({ ...form, account_id: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white h-10 outline-none">
              <option value="" className="bg-[#0B0B0E]">Choisir un compte...</option>
              {accounts.map(a => <option key={a.id} value={a.id} className="bg-[#0B0B0E]">{a.name}</option>)}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs">Employé (optionnel)</Label>
            <select data-testid="report-sale-chatter" value={form.chatter_id} onChange={e => setForm({ ...form, chatter_id: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white h-10 outline-none">
              <option value="" className="bg-[#0B0B0E]">Non attribué</option>
              {team.map(t => <option key={t.id} value={t.id} className="bg-[#0B0B0E]">{t.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Client</Label><Input data-testid="report-sale-client" value={form.client} onChange={e => setForm({ ...form, client: e.target.value })} className="bg-white/5 border-white/10 text-white h-10" /></div>
            <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Montant (€)</Label><Input data-testid="report-sale-amount" type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} className="bg-white/5 border-white/10 text-white h-10" /></div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs">Type</Label>
            <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white h-10 outline-none">
              {[['purchase', 'achat'], ['donation', 'don'], ['wishlist', 'wishlist'], ['paid_chat', 'chat payant']].map(([v, l]) => <option key={v} value={v} className="bg-[#0B0B0E]">{l}</option>)}
            </select>
          </div>
          <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Lien / article (optionnel)</Label><Input value={form.link} onChange={e => setForm({ ...form, link: e.target.value })} className="bg-white/5 border-white/10 text-white h-10" /></div>
          <Button data-testid="report-sale-submit" className="w-full bg-indigo-500 hover:bg-indigo-600" onClick={save} disabled={saving}>{saving ? 'Déclaration...' : 'Déclarer la vente'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Sel({ label, value, onChange, options, testid }) {
  return (
    <select data-testid={testid} value={value} onChange={e => onChange(e.target.value)} className="bg-white/5 border border-white/10 rounded-lg px-3 text-xs text-white h-9 outline-none focus:border-indigo-500/50">
      <option value="all" className="bg-[#0B0B0E]">{label}</option>
      {options.map(([v, l]) => <option key={v} value={v} className="bg-[#0B0B0E]">{l}</option>)}
    </select>
  );
}
